import { SupervisionTimeCalculationsInvalidator } from "@/composables/useSupervisionTimeCalculations";
import type { QueryClient } from "@tanstack/vue-query";

/**
 * The app-wide QueryClient, captured on initialization so non-component code
 * (e.g. Vuex actions) can invalidate queries without an injection context.
 */
let queryClientRef: QueryClient | null = null;

/**
 * Initialize query invalidation utilities with the QueryClient instance.
 * This should be called once during app initialization.
 */
export function initializeQueryInvalidation(queryClient: QueryClient) {
  queryClientRef = queryClient;
  SupervisionTimeCalculationsInvalidator.setQueryClient(queryClient);
}

/**
 * Invalidate the cached closed-day (lukkedage) event lists used by
 * useClosedDayCount, so the weekly target / estimated rest recompute after
 * calendar events change. Called wherever the events store cache is reset.
 *
 * The count reads through eventsService.fetchEvents, which has its own
 * ["events", ...] query cache, so dropping only the outer key would recount
 * from the same stale event list.
 */
export function invalidateClosedDayEvents() {
  queryClientRef?.invalidateQueries({ queryKey: ["closed-day-events"] });
  queryClientRef?.invalidateQueries({ queryKey: ["events"] });
}

/**
 * Utility functions to call after mutations that affect supervision time calculations.
 * These should be integrated into your existing mutation handlers.
 */
export const invalidateSupervisionCalculations = {
  /**
   * Call after any mutation that affects supervision time calculations
   * Example: Absence, Duty, Event, Employment
   * @param employmentIds - The employment IDs that were affected by the mutation
   */
  afterSupervisionTimeCalculationsMutation: async (employmentIds: number[]) => {
    await SupervisionTimeCalculationsInvalidator.onSupervisionTimeCalculationsChanged(
      employmentIds
    );
  }
};
